import {FabIconPresetAssetUrls} from '@/lib/fab-icon-presets'
import {DEFAULT_FONT_FAMILY, resolveShellFontFamily} from '@/sdk/fonts'
import {
  FormFactors,
  resolveSuggestionOrder,
  SuggestionOrderValues,
  type CenterBarToDrawerConfig,
  type EmbeddedConfig,
  type EmbeddedToSheetConfig,
  type ExternalTriggerConfig,
  type FabConfig,
  type ShellConfig,
  type SuggestionOrder,
} from '@/sdk/types'

/** Max serialized preview config size accepted from query params or script attributes. */
export const PREVIEW_SHELL_CONFIG_MAX_LENGTH = 20_000

const DefaultFabIconUrl = FabIconPresetAssetUrls.sparkle

/**
 * Mirror of `DefaultChatPlaceholderText`. Duplicated because this module ships in
 * the client SDK bundle and must stay free of the zod-heavy
 * `experience-design-config` import graph.
 */
const DefaultPlaceholderText = 'What can I help you with?'

const LegacyFormFactorMap: Record<string, string> = {
  inline: 'embedded',
  'floating-button': 'fab',
}

const StudioFormFactorMap: Record<string, string> = {
  'embedded-to-embedded': 'embedded',
  'fab-to-sheet': 'fab',
  'external-trigger-to-sheet': 'external-trigger',
}

const StartingStateKindValues = {
  Chips: 'chips',
  ShowStores: 'show-stores',
} as const

type ParsedStartingState = {
  kind?: (typeof StartingStateKindValues)[keyof typeof StartingStateKindValues]
  text: string | null
  options: string[] | null
  defaultLocationTags?: string[] | null
}

type RawShellConfig = Record<string, unknown> & {
  startingState?: {
    kind?: unknown
    text?: unknown
    options?: unknown
    defaultLocationTags?: unknown
  }
}

function stringArrayOrNull(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null
  }
  const strings = value.filter((entry): entry is string => typeof entry === 'string')
  return strings.length > 0 ? strings : null
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function parseStartingState(data: RawShellConfig['startingState']): ParsedStartingState | undefined {
  if (!data) {
    return undefined
  }

  const kind =
    data.kind === StartingStateKindValues.ShowStores
      ? StartingStateKindValues.ShowStores
      : data.kind === StartingStateKindValues.Chips
        ? StartingStateKindValues.Chips
        : undefined

  const defaultLocationTags = stringArrayOrNull(data.defaultLocationTags)

  return {
    ...(kind ? {kind} : {}),
    text: stringOrNull(data.text),
    options: Array.isArray(data.options)
      ? data.options.filter((option): option is string => typeof option === 'string')
      : null,
    ...(defaultLocationTags ? {defaultLocationTags} : {}),
  }
}

function percentOrDefault(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : 0
}

function revealAfterPageScrollOrDefault(data: RawShellConfig): boolean {
  if (typeof data.revealAfterPageScroll === 'boolean') {
    return data.revealAfterPageScroll
  }

  const scrollDepth = data.scrollDepth
  if (scrollDepth && typeof scrollDepth === 'object' && !Array.isArray(scrollDepth)) {
    const value = (scrollDepth as Record<string, unknown>).value
    return typeof value === 'number' && Number.isFinite(value) && value > 0
  }

  return percentOrDefault(data.scrollDepthPercent) > 0
}

function animationStyleOrDefault(value: unknown): EmbeddedToSheetConfig['animationStyle'] {
  return value === 'ember' || value === 'nebula' || value === 'beam' || value === 'none' ? value : 'none'
}

/** Normalize legacy/studio form factor strings to SDK runtime values. */
export function resolveFormFactor(raw: string | undefined | null): string {
  const value = raw ?? FormFactors.Embedded
  const mapped = LegacyFormFactorMap[value]
  if (mapped) {
    console.warn(`[Anagram] Form factor "${value}" is deprecated. Use "${mapped}" instead.`)
    return mapped
  }
  return StudioFormFactorMap[value] ?? value
}

function resolvePanelLocation(value: unknown, formFactor: string): 'left' | 'right' | 'center' {
  if (value === 'left' || value === 'right' || value === 'center') {
    return value
  }
  if (value != null && value !== '') {
    console.warn(`[Anagram] Invalid panelLocation "${String(value)}"; using launcher default instead.`)
  }
  return formFactor === FormFactors.CenterBarToDrawer ? 'center' : 'right'
}

function parseSuggestionOrder(value: unknown): SuggestionOrder | undefined {
  if (value === SuggestionOrderValues.InputFirst || value === SuggestionOrderValues.SuggestionsFirst) {
    return value
  }
  return undefined
}

/** Parse a shell config JSON object returned by `/api/sdk/config/*` into typed SDK config. */
export function parseShellConfig(data: RawShellConfig): ShellConfig {
  const formFactor = resolveFormFactor(typeof data.formFactor === 'string' ? data.formFactor : null)
  const panelLocation = resolvePanelLocation(data.panelLocation, formFactor)
  const base = {
    accentColor: stringOrNull(data.accentColor),
    assistantImageUrl: stringOrNull(data.assistantImageUrl),
    fontFamily: resolveShellFontFamily(stringOrNull(data.fontFamily) ?? DEFAULT_FONT_FAMILY),
    placeholderText: stringOrNull(data.placeholderText) ?? DefaultPlaceholderText,
  }
  const panel = {
    panelTitle: stringOrNull(data.panelTitle) ?? '',
    panelImageUrl: stringOrNull(data.panelImageUrl),
    panelLocation,
  }

  if (
    formFactor === FormFactors.EmbeddedToSheet ||
    formFactor === FormFactors.CenterBarToDrawer ||
    formFactor === FormFactors.ExternalTrigger
  ) {
    const sheetConfig = {
      ...base,
      ...panel,
      formFactor,
      startingState: parseStartingState(data.startingState) ?? {text: null, options: null},
      theme: typeof data.theme === 'object' && data.theme !== null ? (data.theme as Record<string, string>) : {},
      animationStyle: animationStyleOrDefault(data.animationStyle),
      showInputShadow: typeof data.showInputShadow === 'boolean' ? data.showInputShadow : true,
      showPoweredByAnagram: typeof data.showPoweredByAnagram === 'boolean' ? data.showPoweredByAnagram : true,
    }

    if (formFactor === FormFactors.CenterBarToDrawer) {
      return {
        ...sheetConfig,
        formFactor,
        revealAfterPageScroll: revealAfterPageScrollOrDefault(data),
      } satisfies CenterBarToDrawerConfig
    }

    if (formFactor === FormFactors.ExternalTrigger) {
      return {
        ...sheetConfig,
        formFactor,
      } satisfies ExternalTriggerConfig
    }

    return {
      ...sheetConfig,
      formFactor,
      suggestionOrder: resolveSuggestionOrder(parseSuggestionOrder(data.suggestionOrder)),
    } satisfies EmbeddedToSheetConfig
  }

  if (formFactor === FormFactors.Fab) {
    const startingState = parseStartingState(data.startingState)
    return {
      ...base,
      ...panel,
      formFactor: FormFactors.Fab,
      ...(typeof data.theme === 'object' && data.theme !== null ? {theme: data.theme as Record<string, string>} : {}),
      fabColor: stringOrNull(data.fabColor) ?? '#000000',
      fabIconUrl: stringOrNull(data.fabIconUrl) ?? DefaultFabIconUrl,
      ...(startingState ? {startingState} : {}),
    } satisfies FabConfig
  }

  const startingState = parseStartingState(data.startingState)
  const embedded: EmbeddedConfig = {
    ...base,
    formFactor: FormFactors.Embedded,
    ...(startingState ? {startingState} : {}),
    suggestionOrder: resolveSuggestionOrder(parseSuggestionOrder(data.suggestionOrder)),
  }
  return embedded
}

/**
 * Parse serialized preview shell config from a script attribute or live-page query param.
 * Returns null when preview mode is off, input is malformed, or exceeds size limits.
 */
export function parsePreviewShellConfig(
  raw: string | string[] | undefined | null,
  isFromPreview: boolean
): ShellConfig | null {
  if (!isFromPreview || Array.isArray(raw) || !raw || raw.length > PREVIEW_SHELL_CONFIG_MAX_LENGTH) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return parseShellConfig(parsed)
  } catch {
    return null
  }
}
